/**
 * usePaymentOrders — sol·licituds de cobrament (enllaços de pagament, `plans/stripe-payment-links.md` F5).
 *
 * API d'ADMIN: `GET /payments/orders/preview`, `GET /payments/orders/outstanding` (el pendent
 * d'una reserva segons el llibre del PMS, per al popover), `GET /payments/orders`,
 * `POST /payments/orders` i
 * `POST /payments/orders/:id/{cancel,supersede,mark-sent}`, i `POST
 * /payments/orders/:id/reread-provider` («torna a llegir Stripe»: només apunta el que digui el
 * proveïdor, no decideix res). Res es crea sol: cada crida surt d'un
 * botó. En acabar una escriptura es refresquen les sol·licituds, el calendari (la franja de
 * pagament canvia amb «enviat») i la fitxa; si falla, un toast amb el perquè.
 */

import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  CreatePaymentOrderInput,
  PaymentOrderActionResult,
  PaymentOrderPreview,
  PaymentOrderSentVia,
  PaymentOrderView,
  ProviderRereadOutcome,
  ProviderRereadResult,
  ReservationPaymentOutstanding,
} from '@guilleumes/types';
import { api } from '@/services/api';
import { orderWarningTexts, paymentOrderErrorText } from '@/lib/payment-orders';

export const URGENT_WARNING_TOAST_MS = 30_000;

export const paymentOrderKeys = {
  all: ['payment-orders'] as const,
  byReservation: (reservationId: string) =>
    ['payment-orders', 'by-reservation', reservationId] as const,
  preview: (reservationId: string) => ['payment-orders', 'preview', reservationId] as const,
  /** Sota `all`: qualsevol acció sobre una sol·licitud el refresca. */
  outstanding: (reservationId: string) =>
    ['payment-orders', 'outstanding', reservationId] as const,
};

// ─── Crides (fora dels hooks, per provar-les amb `api` mockejat) ─────────────

export async function fetchPaymentOrders(reservationId: string): Promise<PaymentOrderView[]> {
  const res = await api.get<PaymentOrderView[]>('/payments/orders', { params: { reservationId } });
  return res.data;
}

export async function fetchPaymentOrderPreview(reservationId: string): Promise<PaymentOrderPreview> {
  const res = await api.get<PaymentOrderPreview>('/payments/orders/preview', {
    params: { reservationId },
  });
  return res.data;
}

/** El pendent d'una reserva segons el llibre del PMS (cobraments per enllaç). */
export async function fetchReservationOutstanding(
  reservationId: string,
): Promise<ReservationPaymentOutstanding> {
  const res = await api.get<ReservationPaymentOutstanding>('/payments/orders/outstanding', {
    params: { reservationId },
  });
  return res.data;
}

export async function createPaymentOrder(
  body: CreatePaymentOrderInput,
): Promise<PaymentOrderActionResult> {
  return (await api.post<PaymentOrderActionResult>('/payments/orders', body)).data;
}

export type PaymentOrderAction =
  | { kind: 'cancel'; reason?: string }
  | { kind: 'supersede' }
  | { kind: 'mark-sent'; via: PaymentOrderSentVia };

export interface PaymentOrderActionVariables {
  orderId: string;
  action: PaymentOrderAction;
}

export async function sendPaymentOrderAction({
  orderId,
  action,
}: PaymentOrderActionVariables): Promise<PaymentOrderActionResult> {
  const url = `/payments/orders/${encodeURIComponent(orderId)}`;
  switch (action.kind) {
    case 'cancel': {
      const reason = action.reason?.trim();
      return (await api.post<PaymentOrderActionResult>(`${url}/cancel`, reason ? { reason } : {}))
        .data;
    }
    case 'supersede':
      return (await api.post<PaymentOrderActionResult>(`${url}/supersede`)).data;
    case 'mark-sent':
      return (await api.post<PaymentOrderActionResult>(`${url}/mark-sent`, { via: action.via }))
        .data;
  }
}

/** El que es diu a la persona després de rellegir. Mai «s'ha desbloquejat»: només què hi ha. */
export const REREAD_OUTCOME_TEXT: Record<ProviderRereadOutcome, string> = {
  BOOKED: 'Stripe diu que està pagat: el cobrament ja és apuntat.',
  IN_PROGRESS: 'Stripe diu que el pagament encara està en curs. No s’ha canviat res més.',
  FAILED: 'Stripe diu que aquell pagament ha fallat.',
  NOT_PAID: 'Stripe diu que aquell enllaç no s’ha pagat.',
  NOTHING_TO_READ: 'Aquest enllaç no té cap pagament a Stripe per llegir.',
  UNREADABLE: 'Stripe no ha contestat. No s’ha canviat res: torna-ho a provar més tard.',
};

export async function rereadPaymentOrderProvider(orderId: string): Promise<ProviderRereadResult> {
  const url = `/payments/orders/${encodeURIComponent(orderId)}/reread-provider`;
  return (await api.post<ProviderRereadResult>(url)).data;
}

/** Tot el que pot haver canviat després de tocar una sol·licitud. */
export function invalidatePaymentOrderQueries(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
): void {
  void queryClient.invalidateQueries({ queryKey: paymentOrderKeys.all });
  // «Enviat» escriu `paymentRequestedAt/Via` a la reserva: la franja de la card i el popover.
  void queryClient.invalidateQueries({ queryKey: ['calendar-bookings'], exact: false });
  void queryClient.invalidateQueries({ queryKey: ['reservation-full'], exact: false });
}

const ACTION_DONE_TEXT: Record<PaymentOrderAction['kind'], string> = {
  cancel: 'Enllaç de pagament cancel·lat',
  supersede: 'Enllaç de pagament retirat',
  'mark-sent': 'Marcat com enviat',
};

/** L'avís d'una acció que s'ha fet però amb alguna cosa a mirar. */
export function warnAboutResult(result: PaymentOrderActionResult): void {
  for (const warning of orderWarningTexts(result.warnings)) {
    // «L'anterior ja estava pagat»: vermell i mig minut a la vista, que no passi per alt.
    if (warning.urgent) toast.error(warning.text, { duration: URGENT_WARNING_TOAST_MS });
    else toast.warning(warning.text);
  }
}

// ─── Hooks ───────────────────────────────────────────────────────────────────

/** Les sol·licituds d'una reserva (la més nova primer). */
export function usePaymentOrders(reservationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: paymentOrderKeys.byReservation(reservationId ?? ''),
    queryFn: () => fetchPaymentOrders(reservationId as string),
    enabled: enabled && !!reservationId,
    staleTime: 30_000,
  });
}

/**
 * El pendent d'una reserva segons el llibre del PMS (decisió del 14-OCT-2026): el popover l'ensenya
 * en lloc del `balance` de Beds24 quan la reserva té cobraments per enllaç. Només ADMIN (qui crida
 * passa `enabled`); un error no fa soroll: el popover es queda amb el de Beds24.
 */
export function useReservationOutstanding(reservationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: paymentOrderKeys.outstanding(reservationId ?? ''),
    queryFn: () => fetchReservationOutstanding(reservationId as string),
    enabled: enabled && !!reservationId,
    staleTime: 30_000,
    retry: false,
  });
}

/** La proposta per defecte. Només es demana amb el diàleg obert, i sempre fresca. */
export function usePaymentOrderPreview(reservationId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: paymentOrderKeys.preview(reservationId ?? ''),
    queryFn: () => fetchPaymentOrderPreview(reservationId as string),
    enabled: enabled && !!reservationId,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    // Un refetch en tornar a la pestanya tornaria a omplir el formulari a mig editar.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useCreatePaymentOrder() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createPaymentOrder,
    onSuccess: (result: PaymentOrderActionResult) => {
      invalidatePaymentOrderQueries(queryClient);
      warnAboutResult(result);
    },
    onError: (error: unknown) => {
      toast.error(paymentOrderErrorText(error));
    },
  });
}

/**
 * Després de rellegir: es refresca tot el que pot haver canviat (la sol·licitud pot haver passat a
 * pagada, i amb ella la franja del calendari i la fitxa) i es diu què ha trobat el proveïdor.
 */
export function afterProviderReread(
  queryClient: Pick<QueryClient, 'invalidateQueries'>,
  result: ProviderRereadResult,
): void {
  invalidatePaymentOrderQueries(queryClient);
  const text = REREAD_OUTCOME_TEXT[result.outcome] ?? REREAD_OUTCOME_TEXT.UNREADABLE;
  if (result.outcome === 'BOOKED') toast.success(text);
  else if (result.outcome === 'UNREADABLE') toast.error(text);
  else toast.info(text);
}

/** «Torna a llegir Stripe»: apunta el que digui el proveïdor i ensenya què ha trobat. */
export function useRereadPaymentOrderProvider() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: rereadPaymentOrderProvider,
    onSuccess: (result: ProviderRereadResult) => afterProviderReread(queryClient, result),
    onError: (error: unknown) => {
      toast.error(paymentOrderErrorText(error));
      void queryClient.invalidateQueries({ queryKey: paymentOrderKeys.all });
    },
  });
}

/** Cancel·lar, retirar (substituïda) i marcar com enviada. */
export function usePaymentOrderAction() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: sendPaymentOrderAction,
    onSuccess: (result: PaymentOrderActionResult, variables: PaymentOrderActionVariables) => {
      invalidatePaymentOrderQueries(queryClient);
      toast.success(ACTION_DONE_TEXT[variables.action.kind]);
      warnAboutResult(result);
    },
    onError: (error: unknown) => {
      toast.error(paymentOrderErrorText(error));
      // `ORDER_CHANGED` / `INVALID_TRANSITION` / `ORDER_PAST_DUE`: el que es veu ja no és bo.
      void queryClient.invalidateQueries({ queryKey: paymentOrderKeys.all });
    },
  });
}
